import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { FileText, Receipt, Package, TrendingUp, TrendingDown, ArrowRight, Sparkles } from "lucide-react";

const API = `${window.location.origin}/api`;

const STATUS_COLOR = {
  accepted: "bg-green-100 text-green-800",
  paid: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  draft: "bg-gray-100 text-gray-800",
  sent: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
  declined: "bg-red-100 text-red-800",
  converted: "bg-purple-100 text-purple-800",
  converted_to_job: "bg-purple-100 text-purple-800",
  complete: "bg-green-100 text-green-800",
  partial: "bg-blue-100 text-blue-800",
  overdue: "bg-red-100 text-red-800",
};

export const ProjectCommandCenter = ({ authHeaders, jobId, jobStatus, onNavigate, onInvoiceGenerated }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchRelated = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jobs/${jobId}/related`, authHeaders);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, jobId]);

  useEffect(() => { fetchRelated(); }, [fetchRelated]);

  const handleGenerateInvoice = async () => {
    if (!window.confirm("Create a draft invoice pre-filled with job details and received materials?")) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/jobs/${jobId}/generate-invoice`, {}, authHeaders);
      toast.success(`Invoice created with ${res.data.line_items_count} line(s)`);
      if (onInvoiceGenerated) onInvoiceGenerated(res.data.invoice);
      fetchRelated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  if (loading && !data) return <div className="text-xs text-muted-foreground py-2">Loading related records…</div>;
  if (!data) return null;

  const { quotes = [], invoices = [], materials = [], profitability = {} } = data;
  const profit = profitability.profit || 0;
  const profitIsPositive = profit >= 0;
  const showGenerateBtn = String(jobStatus || '').toLowerCase().includes('complete') && invoices.length === 0;

  return (
    <div className="space-y-3" data-testid="project-command-center">
      {/* Profitability card */}
      <div className={`border rounded-lg p-3 ${profitIsPositive ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'}`} data-testid="job-profitability-card">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 font-medium text-sm">
            {profitIsPositive ? <TrendingUp className="h-4 w-4 text-green-700" /> : <TrendingDown className="h-4 w-4 text-red-700" />}
            Profitability
          </div>
          <Badge variant="outline" className="text-xs">{profitability.revenue_source || 'estimate'}</Badge>
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Revenue</div>
            <div className="font-semibold" data-testid="profit-revenue">£{(profitability.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Material cost</div>
            <div className="font-semibold" data-testid="profit-material-cost">£{(profitability.material_cost_received || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            {profitability.material_cost_all > profitability.material_cost_received && (
              <div className="text-[10px] text-muted-foreground">+£{(profitability.material_cost_all - profitability.material_cost_received).toFixed(2)} pending</div>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Estimated profit</div>
            <div className={`font-bold ${profitIsPositive ? 'text-green-700' : 'text-red-700'}`} data-testid="profit-value">
              £{profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-xs font-normal ml-1">({profitability.margin_pct || 0}%)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Related documents */}
      <div className="border rounded-lg p-3 bg-muted/20" data-testid="related-documents">
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Related documents
          </div>
          <div className="flex gap-2">
            {quotes.length > 0 && (() => {
              // Prefer the newest quote that isn't rejected/declined so a rejected v1 never
              // hijacks the deep-link when a live v2 exists. Quotes arrive sorted newest-first.
              const target = quotes.find(q => !['rejected', 'declined', 'cancelled'].includes(String(q.status || '').toLowerCase())) || quotes[0];
              return (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onNavigate && onNavigate('quote', target.id)}
                  data-testid="open-quote-btn"
                  title={`Jump to quote ${target.reference || target.id.slice(0,8)}`}
                >
                  🔗 Open Quote
                </Button>
              );
            })()}
            {showGenerateBtn && (
              <Button size="sm" onClick={handleGenerateInvoice} disabled={generating} data-testid="generate-invoice-btn">
                {generating ? "Generating…" : "Generate invoice"}
              </Button>
            )}
          </div>
        </div>

        {/* Quotes */}
        <div className="mb-2">
          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            <FileText className="h-3 w-3" /> Quotes ({quotes.length})
          </div>
          {quotes.length === 0 ? (
            <div className="text-xs text-muted-foreground pl-4">—</div>
          ) : quotes.map(q => (
            <button
              key={q.id}
              className="w-full flex items-center gap-2 justify-between text-sm py-1.5 px-2 rounded hover:bg-white"
              onClick={() => onNavigate && onNavigate('quote', q.id)}
              data-testid={`related-quote-${q.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground">{q.reference || q.id.slice(0, 8)}</span>
                <Badge variant="outline" className={`${STATUS_COLOR[q.status] || ''} text-[10px]`}>{q.status || 'draft'}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">£{(q.total || 0).toFixed(2)}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>

        {/* Invoices */}
        <div className="mb-2">
          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            <Receipt className="h-3 w-3" /> Invoices ({invoices.length})
          </div>
          {invoices.length === 0 ? (
            <div className="text-xs text-muted-foreground pl-4">—</div>
          ) : invoices.map(inv => (
            <button
              key={inv.id}
              className="w-full flex items-center gap-2 justify-between text-sm py-1.5 px-2 rounded hover:bg-white"
              onClick={() => onNavigate && onNavigate('invoice', inv.id)}
              data-testid={`related-invoice-${inv.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground">{inv.reference || inv.id.slice(0, 8)}</span>
                <Badge variant="outline" className={`${STATUS_COLOR[inv.status] || ''} text-[10px]`}>{inv.status || 'draft'}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">£{(inv.total || 0).toFixed(2)}</span>
                {inv.amount_paid > 0 && inv.amount_paid < (inv.total || 0) && (
                  <span className="text-xs text-green-700">(£{inv.amount_paid.toFixed(2)} paid)</span>
                )}
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>

        {/* Materials */}
        <div>
          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            <Package className="h-3 w-3" /> Materials ({materials.length})
          </div>
          {materials.length === 0 ? (
            <div className="text-xs text-muted-foreground pl-4">—</div>
          ) : materials.slice(0, 6).map(m => (
            <div
              key={m.id}
              className="flex items-center gap-2 justify-between text-sm py-1 px-2"
              data-testid={`related-material-${m.id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="truncate">{m.name}</span>
                <Badge variant="outline" className={`${STATUS_COLOR[m.status] || ''} text-[10px]`}>{(m.status || 'to_order').replace('_', ' ')}</Badge>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">£{(m.total_cost || 0).toFixed(2)}</span>
            </div>
          ))}
          {materials.length > 6 && (
            <div className="text-xs text-muted-foreground pl-2 mt-1">+{materials.length - 6} more — see Materials tab</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCommandCenter;
